import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import svLocale from '@fullcalendar/core/locales/sv'
import timeGridPlugin from '@fullcalendar/timegrid'
import { EventClickArg, EventInput } from '@fullcalendar/core'
import {
  Box,
  FormControlLabel,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Workout } from '../providers/workouts'
import { useWorkoutSettings, WorkoutType } from '../providers/workout-settings'

const CALENDAR_SCROLL_KEY = 'public-workout-calendar-scroll-top'
const PAGE_SCROLL_KEY = 'public-workout-calendar-page-scroll'

function findCalendarScroller(container: HTMLDivElement | null) {
  if (!container) return null

  return (
    container.querySelector<HTMLElement>('.fc-timegrid-body .fc-scroller') ??
    container.querySelector<HTMLElement>('.fc-scroller-liquid-absolute') ??
    container.querySelector<HTMLElement>('.fc-scroller')
  )
}

function mapWorkoutToEvent(
  workout: Workout,
  getWorkoutTypeName: (id: string) => string,
  getWorkoutType: (id: string) => WorkoutType,
  isWorkoutTypeBookable: (id: string) => boolean
): EventInput {
  const workoutType = getWorkoutType(workout.type)
  const isBookable = isWorkoutTypeBookable(workout.type)
  const color = workoutType.color ?? '#b67943'

  return {
    id: workout.id,
    title: getWorkoutTypeName(workout.type),
    start: workout.startTime.toDate(),
    end: workout.endTime.toDate(),
    backgroundColor: color,
    borderColor: color,
    textColor: '#ffffff',
    extendedProps: {
      location: workout.location,
      status: isBookable ? '' : 'Abonnerat',
      participants: isBookable
        ? `${workout.participantsCount}/${workout.numberOfPlaces}`
        : '',
    },
  }
}

export default function PublicWorkoutCalendar({
  workouts,
}: {
  workouts: Workout[]
}) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const calendarRef = useRef<HTMLDivElement | null>(null)
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [showFullDay, setShowFullDay] = useState(
    searchParams.get('showFullDay') === '1'
  )
  const { getWorkoutType, getWorkoutTypeName, isWorkoutTypeBookable } =
    useWorkoutSettings()
  const calendarView =
    searchParams.get('calendarView') ??
    (smallScreen ? 'timeGridDay' : 'timeGridWeek')
  const calendarDate = searchParams.get('calendarDate') ?? undefined

  const events = workouts.map((workout) =>
    mapWorkoutToEvent(
      workout,
      getWorkoutTypeName,
      getWorkoutType,
      isWorkoutTypeBookable
    )
  )

  const handleEventClick = (arg: EventClickArg) => {
    const calendarScroller = findCalendarScroller(calendarRef.current)

    if (calendarScroller) {
      sessionStorage.setItem(
        CALENDAR_SCROLL_KEY,
        String(calendarScroller.scrollTop)
      )
    }

    sessionStorage.setItem(PAGE_SCROLL_KEY, String(window.scrollY))

    navigate(`/workouts/${arg.event.id}`, {
      state: {
        returnTo: `/?${searchParams.toString()}`,
      },
    })
  }

  useEffect(() => {
    const calendarScroller = findCalendarScroller(calendarRef.current)

    if (!calendarScroller) return

    const handleScroll = () => {
      sessionStorage.setItem(
        CALENDAR_SCROLL_KEY,
        String(calendarScroller.scrollTop)
      )
    }

    calendarScroller.addEventListener('scroll', handleScroll, { passive: true })
    return () => calendarScroller.removeEventListener('scroll', handleScroll)
  }, [calendarDate, calendarView, showFullDay])

  useEffect(() => {
    const restoreScroll = () => {
      const calendarScroller = findCalendarScroller(calendarRef.current)
      const savedCalendarScroll = sessionStorage.getItem(CALENDAR_SCROLL_KEY)
      const savedPageScroll = sessionStorage.getItem(PAGE_SCROLL_KEY)

      if (calendarScroller && savedCalendarScroll) {
        calendarScroller.scrollTop = Number(savedCalendarScroll)
      }

      if (savedPageScroll) {
        window.scrollTo({ top: Number(savedPageScroll), behavior: 'auto' })
      }
    }

    const timeoutId = window.setTimeout(restoreScroll, 80)
    return () => window.clearTimeout(timeoutId)
  }, [calendarDate, calendarView, showFullDay, workouts.length])

  return (
    <Box
      ref={calendarRef}
      sx={{
        width: '100%',
        py: 2,
        '& .fc': {
          color: 'text.primary',
          fontFamily: 'inherit',
        },
        '& .fc .fc-toolbar': {
          gap: 1,
          alignItems: { xs: 'stretch', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
        },
        '& .fc .fc-toolbar-title': {
          fontSize: { xs: '1.1rem', sm: '1.35rem' },
          fontWeight: 700,
        },
        '& .fc .fc-button': {
          backgroundColor: 'primary.main',
          borderColor: 'primary.main',
          borderRadius: 1,
          boxShadow: 'none',
          color: 'primary.contrastText',
          textTransform: 'none',
        },
        '& .fc .fc-button:hover, & .fc .fc-button:focus': {
          backgroundColor: 'primary.dark',
          borderColor: 'primary.dark',
        },
        '& .fc .fc-button:disabled': {
          backgroundColor: 'action.disabledBackground',
          borderColor: 'action.disabledBackground',
          color: 'text.disabled',
        },
        '& .fc-theme-standard td, & .fc-theme-standard th, & .fc-theme-standard .fc-scrollgrid':
          {
            borderColor: 'divider',
          },
        '& .fc .fc-daygrid-day.fc-day-today, & .fc .fc-timegrid-col.fc-day-today':
          {
            backgroundColor: 'rgba(182, 121, 67, 0.08)',
          },
        '& .fc .fc-event': {
          borderRadius: 1,
          cursor: 'pointer',
          padding: '4px 6px',
        },
        '& .fc .fc-event-main': {
          color: 'inherit',
        },
        '& .fc .fc-timegrid-event .fc-event-main-frame': {
          display: 'block',
        },
        '& .fc .fc-daygrid-event': {
          whiteSpace: 'normal',
        },
        '& .fc .fc-timegrid-event': {
          minHeight: '56px',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showFullDay}
              onChange={(event) => {
                const nextChecked = event.target.checked
                setShowFullDay(nextChecked)
                const nextParams = new URLSearchParams(searchParams)
                nextParams.set('view', 'calendar')
                nextParams.set('showFullDay', nextChecked ? '1' : '0')
                setSearchParams(nextParams, { replace: true })
              }}
            />
          }
          label="Visa hela dygnet"
        />
      </Box>

      <FullCalendar
        allDaySlot={false}
        buttonText={{
          today: 'Idag',
          month: 'Månad',
          week: 'Vecka',
          day: 'Dag',
        }}
        editable={false}
        eventClick={handleEventClick}
        eventContent={(arg) => (
          <Box sx={{ overflow: 'hidden' }}>
            <Typography
              component="div"
              sx={{
                fontSize: '0.6rem',
                fontWeight: 500,
                lineHeight: 1.1,
                opacity: 0.92,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {arg.timeText}
            </Typography>
            <Typography
              component="div"
              sx={{
                fontSize: '0.72rem',
                fontWeight: 700,
                lineHeight: 1.2,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}
            >
              {arg.event.title}
            </Typography>
            <Typography
              component="div"
              sx={{
                fontSize: '0.68rem',
                lineHeight: 1.2,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}
            >
              {arg.event.extendedProps.location}
              {arg.event.extendedProps.status
                ? ` · ${arg.event.extendedProps.status}`
                : arg.event.extendedProps.participants
                ? ` · ${arg.event.extendedProps.participants}`
                : ''}
            </Typography>
          </Box>
        )}
        events={events}
        firstDay={1}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        }}
        height="auto"
        initialDate={calendarDate}
        initialView={calendarView}
        locale={svLocale}
        nowIndicator
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        datesSet={(arg) => {
          const nextParams = new URLSearchParams(searchParams)
          nextParams.set('view', 'calendar')
          nextParams.set('calendarView', arg.view.type)
          nextParams.set('calendarDate', arg.view.currentStart.toISOString())
          nextParams.set('showFullDay', showFullDay ? '1' : '0')
          setSearchParams(nextParams, { replace: true })
        }}
        slotDuration="00:30:00"
        slotLabelFormat={{
          hour: '2-digit',
          minute: '2-digit',
          meridiem: false,
        }}
        slotMinTime={showFullDay ? '00:00:00' : '06:00:00'}
        slotMaxTime={showFullDay ? '24:00:00' : '22:00:00'}
        weekends
      />
    </Box>
  )
}
